import React from "react";
import { useParams } from "react-router-dom";
import cloudStore from "../store/CloudStore";
import { Divider, IconButton, Paper, Typography } from "@material-ui/core";
import CheckboxSetting from "../components/SettingFields/CheckboxSetting";
import NumberSetting from "../components/SettingFields/NumberSetting";
import { updateSpMerchant } from "../store/SpMerchantApi";
import TextSetting from "../components/SettingFields/TextSetting";
import withIntl from "../utils/withIntl";
import MultiTextSetting from "../components/SettingFields/MultiTextSetting";
import OpeningHours from "../components/OpeningHours/OpeningHours";
import {
  AccessTime,
  Brush,
  Computer,
  FileCopy,
  Payment,
  PhoneIphone,
  Receipt,
  Room,
  Settings,
} from "@material-ui/icons";
import AuthorizationWrapper from "../components/AuthorizationWrapper";
import HandheldPasswordSetting from "../components/SettingFields/HandheldPasswordSetting";
import { useDialog } from "../components/Dialogs/DialogProvider";
import CreateSpMerchant from "../components/CreateSalesArea/CreateSpMerchant";
import { smallScreenBoundary } from "../containers/Dashboard";
import PosPaymentMethods from "../components/InhouseSettings/PosPaymentMethods";
import InputAdornment from "@material-ui/core/InputAdornment";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import GuestRegistrationSetting from "../components/InhouseSettings/GuestRegistrationSetting";
import DefaultOrderItemsSetting from "../components/SettingFields/DefaultOrderItemsSetting";
import PaymentMethodInhouse from "../components/PaymentMethods/PaymentMethodInhouse";
import StylingV2 from "../components/StylingSettings/StylingV2";
import { updateTaMerchant } from "../store/TaMerchantApi";

const getSpMerchantSettings = (spMerchant) => {
  let styling = {};
  if (spMerchant.styling && spMerchant.styling !== "null") {
    styling = JSON.parse(spMerchant.styling);
  }
  let info = {};
  if (spMerchant.info && spMerchant.info !== "null") {
    info = spMerchant.info;
  }
  const settingGroups = [
    {
      key: "general_settings",
      icon: <Settings fontSize={"large"} />,
      authenticationName: "inhouse.general_settings",
      settings: [
        { key: "name", value: spMerchant.name, type: "text" },
        { key: "demo", value: spMerchant.demo, type: "boolean" },
        { key: "cache_sp_menu", value: spMerchant.cache_sp_menu, type: "boolean" },
        { key: "cache_sp_receipt", value: spMerchant.cache_sp_receipt, type: "boolean" },
        {
          key: "minutes_between_each_receipt_sync",
          value: spMerchant.minutes_between_each_receipt_sync,
          type: "text_nullable",
        },
        { key: "iframe", value: styling["iframe"], type: "boolean", newDataKeys: ["styling", "iframe"] },
        { key: "use_menu_tags", value: spMerchant.use_menu_tags, type: "boolean" },
        { key: "use_queue", value: spMerchant.use_queue, type: "boolean" },
        { key: "is_ordering", value: spMerchant.is_ordering, type: "boolean" },
        {
          key: "welcome_message",
          value: info["welcome_message"],
          type: "multitext",
          newDataKeys: ["info", "welcome_message"],
        },
        {
          key: "info_message",
          value: info["info_message"],
          type: "multitext",
          newDataKeys: ["info", "info_message"],
        },
        {
          key: "closed_message",
          value: info["closed_message"],
          type: "multitext",
          newDataKeys: ["info", "closed_message"],
        },
        { key: "use_pincode", value: spMerchant.use_pincode, type: "boolean" },
        {
          key: "pincode",
          value: spMerchant.pincode,
          type: "text_nullable",
        },
        { key: "use_random_pincode", value: spMerchant.use_random_pincode, type: "boolean" },
      ],
    },

    {
      key: "app_settings",
      authenticationName: "inhouse.app_settings",
      icon: <PhoneIphone fontSize={"large"} />,
      settings: [
        { key: "visible", value: spMerchant.visible, type: "boolean" },
        { key: "can_waiter", value: spMerchant.can_waiter, type: "boolean" },
        { key: "can_move", value: spMerchant.can_move, type: "boolean" },
        { key: "can_pay", value: spMerchant.can_pay, type: "boolean" },
        {
          key: "show_article_images_in_order_history",
          value: info.show_article_images_in_order_history ?? false,
          type: "boolean",
          newDataKeys: ["info", "show_article_images_in_order_history"],
        },
        { key: "show_description", value: spMerchant.show_description, type: "boolean" },
        { key: "show_allergens", value: spMerchant.show_allergens, type: "boolean" },
        { key: "share_order", value: spMerchant.share_order, type: "boolean" },
        { key: "share_order_seconds", value: spMerchant.share_order_seconds, type: "number", min: 5, max: 30 },
        { key: "notifications", value: spMerchant.notifications, type: "boolean" },
        {
          key: "notification_message",
          value: info["notification_message"],
          type: "multitext",
          newDataKeys: ["info", "notification_message"],
        },
      ],
    },
    {
      key: "receipt_settings",
      authenticationName: "inhouse.receipt_settings",
      icon: <Receipt fontSize={"large"} />,
      settings: [
        {
          key: "shielded_receipt",
          value: !spMerchant.shielded_receipt,
          type: "boolean",
          transformValue: (value) => !value,
        },
        { key: "must_buildup_receipt", value: spMerchant.must_buildup_receipt, type: "boolean" },
        { key: "is_every_order_paid", value: spMerchant.is_every_order_paid, type: "boolean" },
        { key: "auto_close_receipts", value: spMerchant.auto_close_receipts, type: "boolean" },
        { key: "maximum_receipts_per_table", value: spMerchant.maximum_receipts_per_table, type: "number" },
        {
          key: "minimum_seconds_between_sp_ordergroups",
          value: spMerchant.minimum_seconds_between_sp_ordergroups,
          type: "number",
        },
      ],
    },
    {
      key: "handheld_settings",
      authenticationName: "inhouse.handheld_settings",
      icon: <Computer fontSize={"large"} />,
      settings: [
        { key: "can_pay_handheld", value: spMerchant.can_pay_handheld, type: "boolean" },
        {
          key: "void_password",
          value: styling["void_password"],
          type: "number_password",
          newDataKeys: ["styling", "void_password"],
        },
        {
          key: "payment_methods",
          value: styling["payment_methods"],
          type: "todo",
        },
      ],
    },
    {
      key: "geolocation_settings",
      authenticationName: "inhouse.geolocation_settings",
      icon: <Room fontSize={"large"} />,
      settings: [
        { key: "user_geo_location", value: spMerchant.user_geo_location, type: "boolean" },
        { key: "maximum_order_distance", value: spMerchant.maximum_order_distance, type: "number" },
      ],
    },
    {
      key: "other_settings",
      authenticationName: "takeaway.others_settings",
      icon: <Settings fontSize={"large"} />,
      settings: [
        {
          key: "default_order_items",
          value: info.default_order_items,
          type: "default_order_items",
          newDataKeys: ["info", "default_order_items"],
        },
        {
          key: "api_url_app",
          value: info.api_url?.app ?? "",
          type: "text",
          newDataKeys: ["info", "api_url", "app"],
        },
      ],
    },
  ];

  return settingGroups;
};

const getNewData = (setting, value) => {
  if (setting.transformValue) {
    value = setting.transformValue(value);
  }
  if (!setting.newDataKeys) {
    return { [setting.key]: value };
  } else {
    let object = {};
    let keys = setting.newDataKeys.reverse();
    keys.forEach((key, index) => {
      if (index === 0) {
        object[key] = value;
      } else {
        object = { [key]: object };
      }
    });
    return object;
  }
};

function InHouseSettings(props) {
  const [spMerchant, setSpMerchant] = React.useState(null);
  const spMerchantId = Number.parseInt(useParams().spMerchantId);
  const [settingGroups, setSettingGroups] = React.useState([]);
  const [openDialog] = useDialog();

  if (spMerchantId !== spMerchant?.id) {
    let spMerchant = cloudStore.merchant.sp_merchants.find((spMerchant) => spMerchant.id === spMerchantId);
    setSpMerchant(spMerchant);
    setSettingGroups(getSpMerchantSettings(spMerchant));
    return null;
  }

  const updateSpMerchantApi = (setting, value) => {
    updateSpMerchant(spMerchant, getNewData(setting, value)).then((spMerchant) => {
      setSettingGroups(getSpMerchantSettings(spMerchant));
      setSpMerchant(spMerchant);
    });
  };

  let paymentMethods = [];
  if (spMerchant?.payment_methods) {
    paymentMethods = spMerchant.payment_methods;
    if (!Array.isArray(paymentMethods)) {
      paymentMethods = [];
    }
  }

  return (
    <div style={{ display: "flex", flexFlow: "wrap" }}>
      {window.innerWidth >= smallScreenBoundary && (
        <AuthorizationWrapper
          permission={"create_sp_merchant"}
          child={
            <IconButton
              style={{ position: "absolute", left: 300, top: 64 }}
              onClick={() => {
                openDialog({ children: <CreateSpMerchant spMerchant={spMerchant} /> });
              }}
            >
              <FileCopy />
            </IconButton>
          }
        />
      )}
      <Paper style={{ width: "450px", padding: "16px", margin: "16px" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <AccessTime fontSize={"large"} />
          <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
            {props.intl.formatMessage({ id: "opening_hours" })}
          </Typography>
        </div>
        <OpeningHours
          openingHours={spMerchant}
          onSave={(newData) => {
            return new Promise((resolve) => {
              updateSpMerchant(spMerchant, newData).then(() => {
                resolve();
              });
            });
          }}
        />
      </Paper>
      {settingGroups.map((settingGroup, index) => {
        return (
          <AuthorizationWrapper
            permission={settingGroup.authenticationName}
            child={(write) => (
              <Paper style={{ width: "450px", padding: "16px", margin: "16px" }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  {settingGroup.icon && settingGroup.icon}
                  <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                    {props.intl.formatMessage({ id: settingGroup.key })}
                  </Typography>
                </div>
                {settingGroup.settings.map((setting, index) => {
                  switch (setting.type) {
                    case "boolean":
                      return (
                        <CheckboxSetting
                          label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                          authenticationName={"sp_merchant." + setting.key}
                          key={setting.key + "-" + spMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            if (value === setting.value) {
                              return;
                            }
                            updateSpMerchantApi(setting, value);
                          }}
                        >
                          <Divider />
                        </CheckboxSetting>
                      );
                    case "number":
                      return (
                        <NumberSetting
                          disabled={!spMerchant.share_order}
                          inputProps={{ min: setting.min ?? undefined, max: setting.max ?? undefined }}
                          label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                          authenticationName={"sp_merchant." + setting.key}
                          key={setting.key + "-" + spMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            if (value === setting.value) {
                              return;
                            }

                            updateSpMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </NumberSetting>
                      );
                    case "text_nullable":
                      return (
                        <TextSetting
                          label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                          authenticationName={"sp_merchant." + setting.key}
                          key={setting.key + "-" + spMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            if (value === setting.value) {
                              return;
                            }

                            if (value === "") {
                              value = null;
                            }

                            updateSpMerchantApi(setting, value);
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <AddCircleIcon color={"primary"} />
                              </InputAdornment>
                            ),
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </TextSetting>
                      );
                    case "number_password":
                      return (
                        <HandheldPasswordSetting
                          label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                          authenticationName={"sp_merchant." + setting.key}
                          key={setting.key + "-" + spMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            if (value === setting.value) {
                              return;
                            }
                            updateSpMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </HandheldPasswordSetting>
                      );
                    case "text":
                      return (
                        <TextSetting
                          label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                          authenticationName={"sp_merchant." + setting.key}
                          key={setting.key + "-" + spMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            if (value === setting.value) {
                              return;
                            }

                            updateSpMerchantApi(setting, value);
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <AddCircleIcon color={"primary"} />
                              </InputAdornment>
                            ),
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </TextSetting>
                      );
                    case "multitext":
                      return (
                        <MultiTextSetting
                          label={props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                          authenticationName={"sp_merchant." + setting.key}
                          key={setting.key + "-" + spMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            if (value === setting.value) {
                              return;
                            }

                            updateSpMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </MultiTextSetting>
                      );
                    case "default_order_items":
                      return (
                        <DefaultOrderItemsSetting
                          label={props.intl.formatMessage({ id: "ta_merchant." + setting.key })}
                          authenticationName={"ta_merchant." + setting.key}
                          key={setting.key + "-" + spMerchant.updated_at}
                          setting={setting}
                          onChange={(value) => {
                            updateSpMerchantApi(setting, value);
                          }}
                        >
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </DefaultOrderItemsSetting>
                      );
                    case "todo":
                      return (
                        <>
                          <Typography variant={"h6"}>
                            Coming soon {props.intl.formatMessage({ id: "sp_merchant." + setting.key })}
                          </Typography>
                          <Divider style={{ marginTop: "4px", marginBottom: "4px" }} />
                        </>
                      );
                    default:
                      return null;
                  }
                })}
              </Paper>
            )}
          />
        );
      })}
      <AuthorizationWrapper permission={"sp_merchant.guest_registration"} child={<GuestRegistrationSetting />} />
      <AuthorizationWrapper
        permission={"sp_merchant.handheld.payment_methods"}
        child={
          <Paper style={{ width: "450px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Payment fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "pos_payment_methods" })}
              </Typography>
            </div>
            <PosPaymentMethods spMerchant={spMerchant} />
          </Paper>
        }
      />

      <AuthorizationWrapper
        permission={"inhouse.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "styling" })}
              </Typography>
            </div>
            <StylingV2
              main={true}
              disableZoom={true}
              styling={JSON.parse(spMerchant.styling)}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateSpMerchant(spMerchant, { styling: styling }).then((spMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"inhouse.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Header image styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColor={false}
              textSecondaryColor={false}
              textDisabledColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontType={false}
              styling={JSON.parse(spMerchant.styling)?.header_image}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateSpMerchant(spMerchant, { styling: { header_image: styling } }).then((spMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"inhouse.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Articlegroup styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textSecondaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(spMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(spMerchant.styling)?.articlegroup}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateSpMerchant(spMerchant, { styling: { articlegroup: styling } }).then((spMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"inhouse.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Article styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColorDefault={JSON.parse(spMerchant.styling)?.palette?.text?.primary}
              textSecondaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(spMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(spMerchant.styling)?.article}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateSpMerchant(spMerchant, { styling: { article: styling } }).then((spMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"inhouse.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Article description styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(spMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(spMerchant.styling)?.article_description}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateSpMerchant(spMerchant, { styling: { article_description: styling } }).then((spMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"inhouse.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Article price styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColor={false}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(spMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(spMerchant.styling)?.article_price}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateSpMerchant(spMerchant, { styling: { article_price: styling } }).then((spMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
      <AuthorizationWrapper
        permission={"inhouse.styling"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Brush fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "Top bar styling" })}
              </Typography>
            </div>
            <StylingV2
              themeType={false}
              primaryColorDefault={JSON.parse(spMerchant.styling)?.palette?.primary?.main}
              secondaryColor={false}
              warningColor={false}
              textPrimaryColorDefault={JSON.parse(spMerchant.styling)?.palette?.text?.primary}
              textSecondaryColor={false}
              textDisabledColor={false}
              backgroundColor={false}
              backgroundColorPaper={false}
              backgroundColorTertiary={false}
              fontTypeDefault={JSON.parse(spMerchant.styling)?.typography?.fontFamily}
              styling={JSON.parse(spMerchant.styling)?.top_navigation}
              onSave={(styling) => {
                return new Promise((resolve, reject) => {
                  updateSpMerchant(spMerchant, { styling: { top_navigation: styling } }).then((spMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />

      <AuthorizationWrapper
        permission={"inhouse.payment_methods"}
        child={
          <Paper style={{ width: "932px", padding: "16px", margin: "16px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Payment fontSize={"large"} />
              <Typography variant={"h6"} style={{ marginLeft: "8px" }}>
                {props.intl.formatMessage({ id: "payment_methods" })}
              </Typography>
            </div>
            <PaymentMethodInhouse
              paymentMethods={paymentMethods}
              onSave={(paymentMethods) => {
                return new Promise((resolve, reject) => {
                  updateSpMerchant(spMerchant, { payment_methods: paymentMethods }).then((spMerchant) => {
                    resolve();
                  });
                });
              }}
            />
          </Paper>
        }
      />
    </div>
  );
}

export default withIntl(InHouseSettings);
